import { MagnifyingGlassIcon, XIcon } from "@phosphor-icons/react";

/*
  The search box that sits where a table's heading used to.

  Every grid gets the same one: same width, same icon, same clear button. It is
  a plain <input type="search"> rather than the shared <Input>, because a form
  field's height and padding don't match a toolbar's — this has to line up with
  the column picker and the pager sitting beside it. Chromium draws its own
  clear "x" on type="search"; that one is hidden so only ours shows.

  Filtering is the page's job, not this component's and not DataTable's: what
  counts as a match is different for a task than for a leave request, and only
  the page knows which fields it put on screen. Pass the filtered array to
  DataTable as usual and the footer's "Showing x-y of z" reports the match
  count for free — which is why the heading and its row-count badge could go.
*/
export default function TableSearch({
  value,
  onChange,
  placeholder = "Search…",
  label,
  testid,
  widthCls = "w-56",
}) {
  return (
    <div className="relative">
      <MagnifyingGlassIcon
        size={13}
        className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400"
      />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={label || placeholder}
        data-testid={testid}
        className={`h-8 ${widthCls} rounded-sm border border-border pl-8 pr-7 text-xs text-zinc-700 placeholder:text-zinc-400 focus:border-[#0176D3] focus:outline-none focus:ring-1 focus:ring-[#0176D3]/30 [&::-webkit-search-cancel-button]:appearance-none`}
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange("")}
          aria-label="Clear search"
          data-testid={testid ? `${testid}-clear` : undefined}
          className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-sm p-1 text-zinc-400 hover:text-zinc-700"
        >
          <XIcon size={11} weight="bold" />
        </button>
      )}
    </div>
  );
}

/*
  True when every whitespace-separated term in the query turns up somewhere in
  the row's searchable text.

  Terms are ANDed, so "amit vacation" narrows to Amit's vacation rather than
  widening to every row mentioning either, and word order doesn't matter — a
  reader typing what they remember shouldn't have to reproduce the column
  layout. Falsy fields are dropped so a caller can pass optional columns
  straight in without guarding each one.
*/
export const matchesQuery = (query, ...fields) => {
  const q = String(query || "").trim().toLowerCase();
  if (!q) return true;
  const hay = fields.filter(Boolean).join(" ").toLowerCase();
  return q.split(/\s+/).every((term) => hay.includes(term));
};
